import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    NavigationListItemLink,
    NavigationChevronIconWrapper,
    NavigationStarFillIconWrapper,
    NavigationStarOutlineIconWrapper,
    NavigationListWrapper,
} from './NavigationPanel.style';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { Panel } from './Panel';
import { RouteViewType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { RightHandSideViewType } from 'src/domains/layouts/state/router/newRouter/rhsRouteType';
import { PromotedEventsNavigationLink } from 'src/domains/layouts/webview/modules/layoutSkeleton/NavigationSidebar/NavigationSidebarPromotedEvents';
import { NavMenuVariantType } from 'src/domains/layouts/config/features/types';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';
import { renderSportIcon } from './NavigationPanel.icons';

type IconType = 'ChevronRight' | 'StarFill' | 'StarOutline';

export interface LinkItemLinkType {
    key: string;
    to: RouteViewType | RightHandSideViewType | string;
    label: JSX.Element | string;
    iconType?: IconType;
    iconClick?: () => void;
}

const renderIcon = (iconParam: IconType | undefined, iconClick?: () => void): React.ReactElement => {
    const icon: IconType = iconParam ?? 'ChevronRight';

    const onClick = (event: React.SyntheticEvent): void => {
        if (iconClick === undefined) {
            return;
        }

        event.stopPropagation();
        event.preventDefault();
        iconClick();
    };

    if (icon === 'ChevronRight') {
        return (
            <NavigationChevronIconWrapper
                position='right'
                onClick={onClick}
            />
        );
    }

    if (icon === 'StarFill') {
        return <NavigationStarFillIconWrapper onClick={onClick} />;
    }

    return <NavigationStarOutlineIconWrapper onClick={onClick} />;
};

const renderItem = (
    link: LinkItemLinkType,
    typeTag: string | undefined,
    googleTagManager: GoogleTagManagerState,
    isCapitalized: boolean,
    toggleMenu: () => void,
    variant: NavMenuVariantType | null
): React.ReactElement => {
    const iconName =
        typeof link.to !== 'string' &&
        'name' in link.to &&
        (link.to.name === 'sport' || link.to.name === 'sport-special')
            ? link.to.id
            : link.key;

    const fireGtmTag = (): void => {
        if (typeTag === 'sport' && typeof link.to !== 'string') {
            if ('name' in link.to && (link.to.name === 'sport' || link.to.name === 'sport-special')) {
                googleTagManager.sportViewTag(link.key, link.to.id);
            }
        }
        toggleMenu();
    };

    if (link.key === 'promotedevents') {
        return <PromotedEventsNavigationLink key={link.key} />;
    }

    return (
        <NavigationListItemLink
            variant={variant}
            key={link.key}
            to={link.to}
            onClick={fireGtmTag}
            isCapitalized={isCapitalized}
        >
            {(variant === NavMenuVariantType.SECONDARY || variant === NavMenuVariantType.TERTIARY) &&
                (typeTag === 'sport' || typeTag === 'help-support') &&
                renderSportIcon(iconName, variant)}
            {link.label}
            {renderIcon(link.iconType, link.iconClick)}
        </NavigationListItemLink>
    );
};

interface PropsType {
    title: string | JSX.Element;
    titleDataTest: string;
    links: Array<LinkItemLinkType>;
    isAzMobile?: boolean;
    type?: 'sports' | 'favourites';
    typeTag?: 'sport' | 'help-support';
}

export const NavigationPanel = observer('NavigationPanel', (props: PropsType): React.ReactElement | null => {
    const { title, titleDataTest, links, isAzMobile, typeTag } = props;
    const common = useCommon();
    const googleTagManager = GoogleTagManagerState.get(common);
    const configState = ConfigComponents.get(common).config;
    const appLayoutsState = AppLayoutsState.get(common);
    const { overlay } = appLayoutsState;

    if (links.length === 0) {
        return null;
    }

    const toggleMenuCallback = isAzMobile === true ? (): void => {} : overlay.toggleMenu;

    return (
        <Panel
            title={title}
            titleDataTest={titleDataTest}
            isAzMobile={isAzMobile}
        >
            <NavigationListWrapper>
                {links.map((link) => {
                    return renderItem(
                        link,
                        typeTag,
                        googleTagManager,
                        configState.sportsNamesCapitalized,
                        toggleMenuCallback,
                        configState.componentVariants.navMenuVariant
                    );
                })}
            </NavigationListWrapper>
        </Panel>
    );
});
